
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';
import { validatePhoneNumber } from '@/utils/phone-validator';
import { validateEmail } from '@/utils/email-validator';
import { supabase } from '@/integrations/supabase/client';
import UserFieldGroup from './UserFieldGroup';
import EmailField from './EmailField';
import AcceptOffersField from './AcceptOffersField';

const formSchema = z.object({
  phone: z.string()
    .min(8, 'Le numéro de téléphone doit contenir 8 chiffres')
    .refine(validatePhoneNumber, 'Numéro de téléphone invalide'),
  email: z.string()
    .email('Adresse email invalide')
    .refine(validateEmail, 'Format d\'email invalide'),
  name: z.string()
    .min(2, 'Le nom doit contenir au moins 2 caractères'),
  acceptOffers: z.boolean()
    .refine(val => val === true, 'Vous devez accepter de recevoir les offres par email')
});

type FormData = z.infer<typeof formSchema>;

interface AdRequestFormProps {
  userData: any;
}

const AdRequestForm: React.FC<AdRequestFormProps> = ({ userData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone: '',
      email: '',
      name: '',
      acceptOffers: false
    }
  });

  // Mettre à jour les valeurs du formulaire quand userData change
  useEffect(() => {
    if (userData) {
      console.log('Mise à jour du formulaire avec les données utilisateur:', userData);
      form.setValue('phone', userData.phone_number || '');
      form.setValue('email', userData.email || '');
      form.setValue('name', userData.name || '');
    }
  }, [userData, form]);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      console.log('Envoi de la demande d\'annonce:', data);
      
      const { data: result, error } = await supabase.functions.invoke('send-ad-request', {
        body: data,
      });
      
      if (error) {
        throw new Error(error.message || 'Erreur lors de l\'envoi');
      }
      
      console.log('Demande envoyée avec succès:', result);
      
      alert("Demande envoyée avec succès ! Nous avons reçu votre demande et vous contacterons bientôt.");
      
      form.reset();
      setIsEditingName(false);
      setIsEditingPhone(false);
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert(error instanceof Error ? error.message : "Une erreur est survenue lors de l'envoi de votre demande.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <UserFieldGroup
          form={form}
          isEditingName={isEditingName}
          setIsEditingName={setIsEditingName}
          isEditingPhone={isEditingPhone}
          setIsEditingPhone={setIsEditingPhone}
        />

        <EmailField form={form} />

        <AcceptOffersField form={form} />

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium"
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Envoi en cours...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              Envoyer la demande
            </div>
          )}
        </Button>
      </form>
    </Form>
  );
};

export default AdRequestForm;
